import { useEffect, useState, useCallback, useRef } from "react";
import { Camera, VideoCamera, Heart, ChatCircle, PushPin, EyeSlash, Trash, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHUNK = 5 * 1024 * 1024;
const TAGS = [["none", "No tag"], ["shipment", "Shipment"], ["tool", "Tool"], ["inventory", "Item"], ["listing", "Listing"], ["vehicle", "Vehicle"]];

export default function LiveFeed({ embedded = false }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [form, setForm] = useState({ caption: "", visibility: "workspace", tag_kind: "none", tag_label: "" });
  const [media, setMedia] = useState([]);
  const [progress, setProgress] = useState(null);
  const [comment, setComment] = useState({});
  const [onShift, setOnShift] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [boardCaption, setBoardCaption] = useState(null);
  const [ytKits, setYtKits] = useState({});
  const fileRef = useRef(null);
  const token = localStorage.getItem("uu_token");

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/feed"), api.get("/feed/shifts")]);
      setPosts(p.data);
      setShifts(s.data);
      setOnShift(s.data.some((x) => x.user_id === user?.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  const upload = async (file) => {
    const chunks = Math.ceil(file.size / CHUNK);
    setProgress({ name: file.name, pct: 1 });
    try {
      const { data: init } = await api.post("/feed/media/init", {
        filename: file.name, content_type: file.type, size: file.size, chunks,
      });
      for (let i = 0; i < chunks; i += 1) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        await api.post("/feed/media/chunk", fd);
        setProgress({ name: file.name, pct: Math.round(((i + 1) / chunks) * 95) });
      }
      const { data: done } = await api.post(`/feed/media/${init.upload_id}/finish`);
      setMedia((m) => [...m, { id: done.media_id, kind: done.kind, name: file.name }]);
      setProgress(null);
      toast.success(`${file.name} ready`);
    } catch (err) {
      setProgress(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const post = async () => {
    try {
      await api.post("/feed", { ...form, media_ids: media.map((m) => m.id), tag_label: form.tag_label || null });
      toast.success("Posted to the feed");
      setForm({ caption: "", visibility: "workspace", tag_kind: "none", tag_label: "" });
      setMedia([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const act = async (fn) => {
    try { await fn(); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const shareUrl = (p) => `${window.location.origin}/p/${p.share_token}`;

  const recordShare = (p, channel) => {
    api.post(`/feed/${p.id}/share`, { channel }).catch(() => {});
  };

  const hasClip = (p) => (p.media || []).some((m) => m.kind === "video");

  const loadYtKit = useCallback(async (p) => {
    if (ytKits[p.id]) return;
    try {
      const { data } = await api.get(`/feed/${p.id}/youtube`);
      setYtKits((k) => ({ ...k, [p.id]: data }));
    } catch { /* no clip, or not allowed */ }
  }, [ytKits]);

  useEffect(() => {
    posts.filter((p) => p.visibility === "public" && hasClip(p)).forEach(loadYtKit);
  }, [posts, loadYtKit]);

  useEffect(() => {
    api.get("/feed/board/caption").then(({ data }) => setBoardCaption(data)).catch(() => {});
  }, []);

  const aiCaption = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/content", {
        kind: "caption",
        brief: form.caption || `A ${form.tag_kind === "none" ? "workshop" : form.tag_kind} update${form.tag_label ? ` about ${form.tag_label}` : ""} from an industrial supply and logistics business`,
      });
      setForm((f) => ({ ...f, caption: data.text }));
      toast.success("Caption written — edit it however you like");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const share = (p) => {
    navigator.clipboard?.writeText(shareUrl(p)).catch(() => {});
    toast.success("Link copied — paste it into Instagram, WhatsApp or anywhere else");
  };

  const shareFacebook = (p) => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(p))}`, "_blank", "noopener");
  };

  const src = (m) => `${API}/feed/media/${m.id}?auth=${token}`;

  return (
    <div data-testid="live-feed-page">
      {!embedded && <PageHeader
        overline="Network"
        title="Live feed"
        description="What's happening on the floor and on the road right now — photos, short videos, who is on shift, and the shipment, tool or listing each post belongs to. New posts appear on their own."
        testid="live-feed-header"
        action={
          <Button
            data-testid="shift-toggle-button"
            variant={onShift ? "default" : "outline"}
            onClick={() => act(() => api.post("/feed/shifts", { on_shift: !onShift }))}
            className="w-full sm:w-auto"
          >
            {onShift ? "On shift now" : "Mark me on shift"}
          </Button>
        }
      />}
      {embedded && (
        <div className="mb-4 flex justify-end">
          <Button
            data-testid="shift-toggle-button-embedded"
            variant={onShift ? "default" : "outline"}
            onClick={() => act(() => api.post("/feed/shifts", { on_shift: !onShift }))}
          >
            {onShift ? "On shift now" : "Mark me on shift"}
          </Button>
        </div>
      )}

      <div className="mb-5 flex flex-col gap-2 border border-border bg-white p-4" data-testid="feed-board-share">
        <p className="overline text-slate-500">Share the board</p>
        <p className="text-sm text-slate-700" data-testid="feed-board-caption">
          {boardCaption?.text || "Public posts on the board — share the lot in one tap."}
        </p>
        <ShareBar url={`${API.replace(/\/api$/, "")}/api/share/feed`}
          text={boardCaption?.text || "Unity & Utility — live from the yard"}
          testid="feed-board-sharebar" instagram
          youtube={{ title: "Live from the yard — Unity & Utility",
            description: boardCaption?.text || "What is happening on our floor and on the road right now.",
            upload_url: "https://studio.youtube.com/channel/UCm0ahsoe5m2agd50-Ox4Uhg/videos" }} />
        {boardCaption?.engine_label && (
          <p className="text-[0.65rem] uppercase tracking-wider text-slate-400">Caption by {boardCaption.engine_label}</p>
        )}
      </div>

      {shifts.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2 border border-border bg-white p-3" data-testid="on-shift-strip">
          <span className="overline text-slate-500">On shift</span>
          {shifts.map((s) => (
            <span key={s.user_id} data-testid={`shift-${s.user_id}`} className="border border-[#A7E3CE] bg-[#E6F7F1] px-2 py-0.5 text-[0.7rem] font-semibold text-[#0B7A5B]">
              {s.name} · {s.role}
            </span>
          ))}
        </div>
      )}

      <div className="mb-6 space-y-3 border border-border bg-white p-4" data-testid="composer">
        <Textarea
          data-testid="caption-input"
          rows={2}
          placeholder="What's going on?"
          value={form.caption}
          onChange={(e) => setForm({ ...form, caption: e.target.value })}
        />
        <div className="grid gap-3 sm:grid-cols-4">
          <div>
            <Label>Tag</Label>
            <Select value={form.tag_kind} onValueChange={(v) => setForm({ ...form, tag_kind: v })}>
              <SelectTrigger data-testid="tag-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TAGS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reference</Label>
            <Input data-testid="tag-label-input" value={form.tag_label} onChange={(e) => setForm({ ...form, tag_label: e.target.value })} placeholder="e.g. SHP-2026-0004" />
          </div>
          <div>
            <Label>Who can see it</Label>
            <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
              <SelectTrigger data-testid="visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="workspace">Workspace only</SelectItem>
                <SelectItem value="public">Public (shareable link)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              data-testid="media-input"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
            />
            <Button variant="outline" data-testid="attach-button" onClick={() => fileRef.current?.click()} className="w-full">
              <Camera size={16} className="mr-1.5" /> Photo / video
            </Button>
          </div>
        </div>
        {progress && (
          <div data-testid="upload-progress" className="text-xs text-slate-500">
            Uploading {progress.name} — {progress.pct}%
            <div className="mt-1 h-1.5 w-full bg-slate-100"><div className="h-1.5 bg-[#002FA7]" style={{ width: `${progress.pct}%` }} /></div>
          </div>
        )}
        {media.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="attached-media">
            {media.map((m) => (
              <span key={m.id} className="border border-border bg-slate-50 px-2 py-1 text-[0.7rem] text-slate-600">
                {m.kind === "video" ? <VideoCamera size={12} className="mr-1 inline" /> : <Camera size={12} className="mr-1 inline" />}
                {m.name}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="outline" size="sm" data-testid="ai-caption-button" onClick={aiCaption} disabled={aiBusy}>
            <Sparkle size={15} className="mr-1.5" /> {aiBusy ? "Writing…" : "Write it for me"}
          </Button>
          <Button data-testid="post-button" onClick={post} disabled={!!progress}>Post</Button>
        </div>
      </div>

      {posts.length === 0 ? (
        <EmptyState testid="no-posts" title="Nothing posted yet" description="Share the first photo or clip from the floor." />
      ) : (
        <div className="space-y-4" data-testid="feed-posts">
          {posts.map((p) => (
            <div key={p.id} data-testid={`post-${p.id}`} className={`border bg-white p-4 ${p.pinned ? "border-[#002FA7]" : "border-border"} ${p.hidden ? "opacity-60" : ""}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <Avatar imageId={p.author_avatar_id} name={p.author_name} size="sm" testid={`post-avatar-${p.id}`} />
                <span className="text-sm font-semibold text-[#0F172A]">{p.author_name}</span>
                <span>{p.author_role}</span>
                <span>{String(p.created_at).slice(0, 16).replace("T", " ")}</span>
                {p.pinned && <span data-testid={`pinned-${p.id}`} className="font-semibold uppercase tracking-wider text-[#002FA7]">Pinned</span>}
                {p.hidden && <span data-testid={`hidden-${p.id}`} className="font-semibold uppercase tracking-wider text-[#B3241B]">Hidden</span>}
                {p.tag_kind !== "none" && (
                  <span className="border border-border bg-slate-50 px-2 py-0.5 text-[0.65rem] uppercase tracking-wider">
                    {p.tag_kind}{p.tag_label ? ` · ${p.tag_label}` : ""}
                  </span>
                )}
              </div>
              {p.caption && <p className="mt-2 text-sm text-slate-700">{p.caption}</p>}
              {(p.media || []).length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {p.media.map((m) => (m.kind === "video" ? (
                    <video key={m.id} data-testid={`video-${m.id}`} controls preload="metadata" className="w-full border border-border bg-black" src={src(m)} />
                  ) : (
                    <img key={m.id} data-testid={`image-${m.id}`} alt={p.caption || "post"} className="w-full border border-border object-cover" src={src(m)} />
                  )))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" variant={p.liked_by_me ? "default" : "outline"} data-testid={`like-${p.id}`} onClick={() => act(() => api.post(`/feed/${p.id}/like`))}>
                  <Heart size={14} className="mr-1" /> {p.like_count}
                </Button>
                <span className="text-xs text-slate-500"><ChatCircle size={13} className="mr-1 inline" />{p.comment_count}</span>
                {p.visibility === "public" && p.share_token && (
                  <ShareBar url={`${API.replace(/\/api$/, "")}/api/share/p/${p.share_token}`}
                    text={p.caption || "Live from the yard"} testid={`share-${p.id}`} compact
                    instagram youtube={ytKits[p.id] || null}
                    onShare={(channel) => recordShare(p, channel)} />
                )}
                {p.shares && Object.keys(p.shares).length > 0 && (
                  <span data-testid={`share-counts-${p.id}`} className="text-[0.65rem] uppercase tracking-wider text-slate-500">
                    shared {Object.entries(p.shares).map(([k, v]) => `${k} ${v}`).join(" · ")}
                  </span>
                )}
                {user?.role === "admin" && (
                  <>
                    <Button size="sm" variant="ghost" data-testid={`pin-${p.id}`} onClick={() => act(() => api.post(`/feed/${p.id}/pin`))}><PushPin size={14} /></Button>
                    <Button size="sm" variant="ghost" data-testid={`hide-${p.id}`} onClick={() => act(() => api.post(`/feed/${p.id}/hide`))}><EyeSlash size={14} /></Button>
                  </>
                )}
                {(p.author_id === user?.id || user?.role === "admin") && (
                  <Button size="sm" variant="ghost" data-testid={`delete-post-${p.id}`} onClick={() => act(() => api.delete(`/feed/${p.id}`))}><Trash size={14} /></Button>
                )}
              </div>
              {(p.comments || []).length > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-border pt-3">
                  {p.comments.map((c, i) => (
                    <div key={c.id} data-testid={`comment-${c.id}`} className="text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1.5">
                        <Avatar imageId={c.author_avatar_id} name={c.author_name} size="sm" testid={`comment-avatar-${p.id}-${i}`} className="h-5 w-5 text-[0.55rem]" />
                        <span className="font-semibold text-[#0F172A]">{c.author_name}</span>
                      </span> {c.body}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Input
                  data-testid={`comment-input-${p.id}`}
                  placeholder="Add a comment"
                  value={comment[p.id] || ""}
                  onChange={(e) => setComment({ ...comment, [p.id]: e.target.value })}
                />
                <Button
                  size="sm"
                  data-testid={`comment-button-${p.id}`}
                  onClick={() => act(async () => {
                    await api.post(`/feed/${p.id}/comments`, { body: comment[p.id] || "" });
                    setComment({ ...comment, [p.id]: "" });
                  })}
                >
                  Send
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
